import api from './api';
import { onSocket } from './socket';

/**
 * One shared copy of the "Action required" list.
 *
 * The bell and the Notification Center used to fetch it independently — each
 * on mount, every minute, on every window focus and on every live push — so a
 * single focus fired the heaviest endpoint in the app twice, and switching
 * views re-fetched what was already on screen. Both now read from here:
 * requests are shared while one is in flight, a copy younger than STALE_MS is
 * served as-is, and a live push or a completed action forces a fresh read.
 */
const STALE_MS = 30 * 1000;
const POLL_MS = 2 * 60 * 1000;

const EMPTY = { rows: [], count: 0, unread: 0, loadedAt: 0, loading: false, error: '' };
let state = EMPTY;
const listeners = new Set();
let inFlight = null;
let refetchAfter = false;
let stopWatching = null;

const emit = () => listeners.forEach(listener => listener(state));
const set = patch => { state = { ...state, ...patch }; emit(); };

export const getActionsState = () => state;

export function refreshActions({ force = false } = {}) {
    if (inFlight) {
        // A forced read during a request that started before the change would
        // return the old list — so go again once it lands.
        if (force) refetchAfter = true;
        return inFlight;
    }
    if (!force && state.loadedAt && Date.now() - state.loadedAt < STALE_MS) return Promise.resolve(state);

    set({ loading: true });
    inFlight = api.get('/notifications/actions')
        .then(res => set({
            rows: res.data.data || [],
            count: res.data.count || 0,
            unread: res.data.unread ?? res.data.count ?? 0,
            loadedAt: Date.now(),
            error: ''
        }))
        .catch(() => set({ error: 'Could not load notifications. Please refresh.' }))
        .finally(() => {
            inFlight = null;
            set({ loading: false });
            if (refetchAfter) { refetchAfter = false; refreshActions({ force: true }); }
        })
        .then(() => state);
    return inFlight;
}

// Local edits (approved, marked read) show immediately; a refresh confirms them.
export const patchActions = update => set(update(state));

export function resetActions() {
    state = EMPTY;
    emit();
}

function watch() {
    const poll = setInterval(() => {
        if (document.visibilityState === 'visible') refreshActions();
    }, POLL_MS);
    const onFocus = () => refreshActions();
    const onChanged = () => refreshActions({ force: true });
    window.addEventListener('focus', onFocus);
    window.addEventListener('notification-actions-changed', onChanged);
    const offSocket = onSocket('notification:new', onChanged);
    return () => {
        clearInterval(poll);
        window.removeEventListener('focus', onFocus);
        window.removeEventListener('notification-actions-changed', onChanged);
        offSocket?.();
    };
}

export function subscribeActions(listener) {
    listeners.add(listener);
    if (!stopWatching) stopWatching = watch();
    listener(state);
    refreshActions();
    return () => {
        listeners.delete(listener);
        if (!listeners.size && stopWatching) { stopWatching(); stopWatching = null; }
    };
}
